import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Save, X as XIcon } from 'lucide-react';
import { useT } from '../i18n.jsx';

// Who signs a notice off before it reaches a family.
//
// Two stages, school-wide. Every school described the same shape: the unit that owns the activity
// looks at it, then the principal signs -- or hands the routine ones to a vice-principal, which is
// why the second stage is a list rather than one person.
//
// Not a configurable chain of N stages: a chain that can be any length is a chain somebody has to
// maintain, and nobody described needing a third.
const STAGES = ['first', 'final'];

// settingsDoc names which metadata document this panel edits. Parent Notice and OLE keep separate
// approval chains -- a school may well want the ECA coordinator signing activity notices and the
// office signing general ones -- so the same panel is pointed at whichever document belongs to the
// module rendering it.
const NoticeApprovalSettings = ({
  schoolId, staff, canManage, isMobile, settingsDoc = 'parent_notice_settings',
}) => {
  const { t } = useT();
  const [approval, setApproval] = useState({ first: { roles: [], staff: [] }, final: { roles: [], staff: [] } });
  const [pick, setPick] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    getDoc(doc(db, 'schools', schoolId, 'metadata', settingsDoc))
      .then((snap) => {
        const a = (snap.exists() ? snap.data().approval : null) || {};
        setApproval({
          first: { roles: a.first?.roles || [], staff: a.first?.staff || [] },
          final: { roles: a.final?.roles || [], staff: a.final?.staff || [] },
        });
      })
      .catch((e) => console.error('Error loading approval settings', e));
  }, [schoolId]);

  const addPerson = (stage) => {
    const email = (pick[stage] || '').trim().toLowerCase();
    if (!email) return;
    setApproval((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], staff: [...new Set([...prev[stage].staff, email])] },
    }));
    setPick((prev) => ({ ...prev, [stage]: '' }));
  };

  const removePerson = (stage, email) =>
    setApproval((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], staff: prev[stage].staff.filter((e) => e !== email) },
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'schools', schoolId, 'metadata', settingsDoc), {
        approval,
        updatedAt: new Date().toISOString(),
        updatedByEmail: (auth.currentUser?.email || '').toLowerCase(),
      }, { merge: true });
      alert(t('noticeApproval.saved'));
    } catch (e) {
      alert(t('noticeApproval.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const label = (email) => {
    const p = (staff || []).find((x) => x.email === email);
    return p?.initial ? `${p.initial} — ${email}` : email;
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: isMobile ? 0 : 'var(--radius-md)',
    padding: isMobile ? '0.8rem' : '1rem',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{t('noticeApproval.title')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.35rem 0 0', maxWidth: '68ch', lineHeight: 1.7 }}>
          {t('noticeApproval.intro')}
        </p>
      </div>

      {STAGES.map((key) => (
        <div key={key} style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          <div>
            <strong style={{ fontSize: '0.95rem' }}>{t(`noticeApproval.stages.${key}Title`)}</strong>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0', lineHeight: 1.6 }}>
              {t(`noticeApproval.stages.${key}Hint`)}
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {approval[key].staff.map((email) => (
              <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', padding: '0.15rem 0.45rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-border)' }}>
                {label(email)}
                {canManage && (
                  <button type="button" onClick={() => removePerson(key, email)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'inherit' }}>
                    <XIcon size={12} />
                  </button>
                )}
              </span>
            ))}
            {approval[key].staff.length === 0 && (
              <span style={{ fontSize: '0.8rem', color: 'var(--color-warning-text)' }}>
                {t('noticeApproval.nobodyNamed')}
              </span>
            )}
          </div>

          {canManage && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select className="input-field" style={{ margin: 0, flex: 1 }}
                      value={pick[key] || ''}
                      onChange={(e) => setPick((prev) => ({ ...prev, [key]: e.target.value }))}>
                <option value="">{t('noticeApproval.addPerson')}</option>
                {(staff || [])
                  .filter((p) => p.email && !approval[key].staff.includes(p.email))
                  .map((p) => (
                    <option key={p.email} value={p.email}>
                      {p.initial ? `${p.initial} — ${p.email}` : p.email}
                    </option>
                  ))}
              </select>
              <button className="btn-outline" onClick={() => addPerson(key)}
                      style={{ width: 'auto', padding: '0.55rem 1rem' }}>{t('common.add')}</button>
            </div>
          )}
        </div>
      ))}

      {canManage && (
        <button className="btn-primary" onClick={handleSave} disabled={saving}
                style={{ width: 'auto', padding: '0.55rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', alignSelf: 'flex-start' }}>
          <Save size={15} /> {saving ? t('common.saving') : t('common.save')}
        </button>
      )}
    </div>
  );
};

export default NoticeApprovalSettings;
